/**
 * Returns a bucketed relative-time label for a given timestamp.
 *
 * Buckets:
 *   < 24 hours      → "In the last day"
 *   < 7 days        → "In the last week"
 *   < 30 days       → "In the last month"
 *   < 90 days       → "In the last 3 months"
 *   < 180 days      → "In the last 6 months"
 *   < 365 days      → "In the last year"
 *   >= 365 days     → "In the last X years" (X = ceil(days/365))
 *
 * @param {string|number|Date} timestamp - ISO-8601 string, epoch ms, or Date object
 * @returns {string|null} Bucketed label, or null if timestamp is invalid/missing
 */
export function getLastUsedBucketLabel(timestamp) {
  if (timestamp == null) {
    return null;
  }

  const ts =
    typeof timestamp === 'string' || typeof timestamp === 'number'
      ? new Date(timestamp)
      : timestamp;

  if (Number.isNaN(ts.getTime())) {
    return null;
  }

  const now = Date.now();
  // Clamp future timestamps to 0 diff
  const diffMs = Math.max(0, now - ts.getTime());

  const ONE_HOUR = 60 * 60 * 1000;
  const ONE_DAY = 24 * ONE_HOUR;

  if (diffMs < ONE_DAY) {
    return 'In the last day';
  }
  if (diffMs < 7 * ONE_DAY) {
    return 'In the last week';
  }
  if (diffMs < 30 * ONE_DAY) {
    return 'In the last month';
  }
  if (diffMs < 90 * ONE_DAY) {
    return 'In the last 3 months';
  }
  if (diffMs < 180 * ONE_DAY) {
    return 'In the last 6 months';
  }
  if (diffMs < 365 * ONE_DAY) {
    return 'In the last year';
  }

  const years = Math.ceil(diffMs / (365 * ONE_DAY));
  if (years === 1) {
    return 'In the last year';
  }
  return `In the last ${years} years`;
}
